import PropTypes from "prop-types";

import { Box, Stack, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

import { useAuth } from "@/features/authentication";
import { PermissionEnum } from "@/features/permissions";
import { formatNumber } from "@/features/shared";
import {
  DownstreamTransactionLinks,
  ProductVariantNameCell,
} from "@/features/ui";

export const ProductVariantsSection = ({ purchaseOrder }) => {
  const { hasPermission } = useAuth();

  if (!purchaseOrder.purchaseOrderProductVariants?.length) {
    return null;
  }

  const columns = [
    {
      field: "name",
      headerName: "Nama",
      flex: 5,
      renderCell: ({ row: { name, sku } }) => (
        <ProductVariantNameCell
          name={name}
          sku={sku}
          linkToStock
          warehouseId={purchaseOrder.warehouse?.id}
          warehouseName={purchaseOrder.warehouse?.name}
        />
      ),
    },
    {
      field: "quantity",
      headerName: "Qty",
      type: "number",
      flex: 1,
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "unitOfMeasurement",
      headerName: "Unit",
      flex: 1,
      valueGetter: (_, row) => row.product.unitOfMeasurement.name,
    },
    {
      field: "goodsReceipts",
      headerName: "Penerimaan",
      flex: 1.5,
      sortable: false,
      renderCell: ({ row }) => (
        <DownstreamTransactionLinks
          items={row.goodsReceipts}
          clickable={hasPermission(PermissionEnum.READ_GOODS_RECEIPTS)}
        />
      ),
    },
    {
      field: "purchaseInvoices",
      headerName: "Faktur",
      flex: 1.5,
      sortable: false,
      renderCell: ({ row }) => (
        <DownstreamTransactionLinks
          items={row.purchaseInvoices}
          clickable={hasPermission(PermissionEnum.READ_PURCHASE_INVOICES)}
        />
      ),
    },
    {
      field: "purchasePayments",
      headerName: "Pembayaran",
      flex: 1.5,
      sortable: false,
      renderCell: ({ row }) => (
        <DownstreamTransactionLinks
          items={row.purchasePayments}
          clickable={hasPermission(PermissionEnum.READ_PURCHASE_PAYMENTS)}
        />
      ),
    },
    {
      field: "costPerUnit",
      headerName: "Harga",
      type: "number",
      flex: 1.5,
      renderCell: ({ row }) => formatNumber(row.costPerUnit),
    },
    {
      field: "totalCost",
      headerName: "Total",
      type: "number",
      flex: 1.5,
      valueGetter: (_, row) =>
        row.quantity * row.costPerUnit - (row.discount ?? 0),
      renderCell: ({ row }) => {
        const { quantity, costPerUnit, discount } = row;
        const total = quantity * costPerUnit - (discount ?? 0);
        const hasDiscount = discount && discount > 0;

        if (!hasDiscount) {
          return <Typography variant="body2">{formatNumber(total)}</Typography>;
        }

        const originalTotal = quantity * costPerUnit;
        return (
          <Box sx={{ py: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {formatNumber(total)}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                textDecoration: "line-through",
                color: "text.secondary",
                fontSize: "0.7rem",
              }}
            >
              {formatNumber(originalTotal)}
            </Typography>
          </Box>
        );
      },
    },
  ];

  const rows = purchaseOrder.purchaseOrderProductVariants;

  return (
    <Stack spacing={2}>
      <DataGrid
        getRowHeight={() => "auto"}
        columns={columns}
        rows={rows}
        disableRowSelectionOnClick
        initialState={{
          sorting: {
            sortModel: [{ field: "name", sort: "asc" }],
          },
        }}
        // hideFooter
      />
    </Stack>
  );
};

ProductVariantsSection.propTypes = {
  purchaseOrder: PropTypes.object.isRequired,
};
